import React, { Component } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { getRoutes, hiveRoutes, routes } from "../../routes";
import "./Base.css";

// Redux
import withShipment from "../../withShipment";
import { userInfoSelector, isAuthenticatedSelector } from "../../redux/selectors/auth";
import { authLogout, refreshUserInfo } from "../../redux/actions/auth";
import Login from "../Login/Login";
import PropTypes from "prop-types";
import PageNotFound from "../../routes/PageNotFound";
import HiveShell from "../../hive/shell/HiveShell";

// Render the 403 variant for routes the user is authenticated for but
// has not been granted access to. Defined here so it carries an
// explicit `code` prop into the shared error component.
const ForbiddenPage = (props) => <PageNotFound {...props} code={403} />;

class Base extends Component {
  componentDidMount() {
    // Re-pull the current user's authorities/routes whenever the tab regains
    // focus, so app grants made elsewhere (or by another admin) take effect
    // without a logout/login cycle.
    //
    // THROTTLED: `window focus` + `visibilitychange` can fire in bursts — a
    // single tab-return fires both, and any portal/menu/context-menu that
    // returns focus to the document (or clicking back from devtools) fires
    // `focus` again. Unthrottled, that spammed /account/getUser on nearly every
    // interaction. Refresh at most once per REFRESH_MIN_MS.
    const REFRESH_MIN_MS = 30000;
    this.lastRefresh = 0;
    this.maybeRefresh = () => {
      if (!this.props.isAuthenticated) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - this.lastRefresh < REFRESH_MIN_MS) return;
      this.lastRefresh = now;
      this.props.refreshUserInfo();
    };
    window.addEventListener('focus', this.maybeRefresh);
    document.addEventListener('visibilitychange', this.maybeRefresh);

    // Navigating between shell screens (Home, My Apps, Store, opening an app)
    // isn't a window-focus event, so the focus-based refresh above never fired
    // on in-app navigation — an access grant/revoke made elsewhere only showed
    // up after a hard refresh. Refresh on route change too, on a short throttle
    // (navigation is user-initiated, not bursty like focus), so the menu, My
    // Apps, and route guards reflect the latest permissions as you move around.
    const NAV_REFRESH_MIN_MS = 4000;
    this.lastNavRefresh = 0;
    this.maybeRefreshOnNav = () => {
      if (!this.props.isAuthenticated) return;
      const now = Date.now();
      if (now - this.lastNavRefresh < NAV_REFRESH_MIN_MS) return;
      this.lastNavRefresh = now;
      // Keep the focus throttle in sync so the two triggers don't double-fetch.
      this.lastRefresh = now;
      this.props.refreshUserInfo();
    };

    if (this.props.isAuthenticated) {
      this.lastRefresh = Date.now();
      this.lastNavRefresh = Date.now();
      this.props.refreshUserInfo();
    }
  }

  componentWillUnmount() {
    if (this.maybeRefresh) {
      window.removeEventListener('focus', this.maybeRefresh);
      document.removeEventListener('visibilitychange', this.maybeRefresh);
    }
  }

  // Gated app routes the current user is allowed to open, plus public routes.
  protectRoutes = () => {
    const userRoutes = this.props.userInfo.routes ? [...this.props.userInfo.routes] : [];
    return getRoutes().filter((route) => userRoutes.includes(route.id)).concat(routes);
  };

  // Defined app paths the user lacks access to → 403 (instead of 404).
  forbiddenRoutes = () => {
    const userRoutes = this.props.userInfo.routes ? [...this.props.userInfo.routes] : [];
    return getRoutes().filter((route) => route.id && !userRoutes.includes(route.id));
  };

  render() {
    const { isAuthenticated, userInfo } = this.props;

    // Unauthenticated: public routes + Login for everything else.
    if (!isAuthenticated) {
      return (
        <Switch>
          {routes
            .filter((r) => r.path !== '/login')
            .map((r) => (
              <Route key={r.path} path={r.path} component={r.component} />
            ))}
          <Route render={() => <Login />} />
        </Switch>
      );
    }

    // Authenticated: the Hive shell wraps the routed content.
    return (
      <HiveShell userInfo={userInfo} logout={this.props.authLogout} onNavigate={this.maybeRefreshOnNav}>
        <Switch>
          {/* Marketplace shell screens (not gated). */}
          {hiveRoutes.map((r) => (
            <Route key={r.path} path={r.path} exact={r.exact} component={r.component} />
          ))}

          {/* A logged-in user hitting /login goes home. */}
          <Route path="/login" render={() => <Redirect to="/" />} />

          {/* Gated app routes + public routes (e.g. file-manager). */}
          {this.protectRoutes()
            .filter((route) => route.path !== '/login')
            .map((route) => (
              <Route key={route.id || route.path} component={route.component} path={route.path} />
            ))}

          {/* Defined paths the user lacks access to → 403 page. */}
          {this.forbiddenRoutes().map((route) => (
            <Route key={`forbidden-${route.id}`} component={ForbiddenPage} path={route.path} />
          ))}

          {/* Catch-all → 404. Must be last. */}
          <Route component={PageNotFound} />
        </Switch>
      </HiveShell>
    );
  }
}

Base.propTypes = {
  userInfo: PropTypes.object,
  isAuthenticated: PropTypes.bool,
};

Base.defaultProps = {
  userInfo: {},
  isAuthenticated: false,
};

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  isAuthenticated: isAuthenticatedSelector(state),
});

const actionCreators = {
  authLogout,
  refreshUserInfo,
};

export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  Base
);
